import { FC, useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { useFinancialWarnings } from 'contexts/FinancialWarningsContext';

import { useDebouncedSave } from 'hooks/useDebounceSave';
import { useExamplePreview } from 'hooks/useExamplePreview';

import { FieldTableRow } from 'components/_basic/Form/FieldTable2/components/FieldTableRow';
import { IPeriodsTableRows } from 'components/BusinessToolForm/constants/periods';
import { ReadOnlyTableRow } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/ReadOnlyTableRow';
import { TableSectionTitle } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TableSectionTitle';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { useUnitChange } from 'components/BusinessToolForm/Sections/FinancialPlan/hooks/useUnitChange';
import { NET_INCOME_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetIncome/utils/form';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import { netIncomePrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import { GLOBAL_PERIOD_NAME } from 'components/BusinessToolForm/Sections/Global/form';
import { PERIODS_OPERATION_TYPES, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IInvestingActivitiesProps } from './types';
import { INVESTING_ACTIVITIES_NAMES, useGetInvestingActivitiesTableHeadings } from './utils/form';
import { FIRST_COLUMN_WIDTH, investingActivitiesPrefix } from '../../../../utils/form';
import { DEFAULT_DEBOUNCE_TIME_DIRECT_FORM } from '../../DirectFormTable/utils/form';

export const InvestingActivities: FC<IInvestingActivitiesProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { isExamplePage } = useExamplePreview();

  const { control, getValues, setValue } = useFormContext();

  const { key } = useUnitChange();
  const { investingActivitiesLinkedFields, totalRowTitle, investingActivitiesVariants } =
    useGetInvestingActivitiesTableHeadings(isExamplePage);
  const { excessiveProceedsFromSalePeriods } = useFinancialWarnings();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const tableName = concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.TABLE);

  const { fields } = useFieldArray({ control, name: tableName });
  const firstGroupFields = fields.slice(0, 2);
  const secondGroupFields = fields.slice(2);

  const columnsNames = getPeriodColumnsNames(period);

  const totalName = concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.TOTAL);
  const purchasePPE = getValues(
    concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.PURCHASE_PROPERTY_PLANT_EQUIPMENT),
  );
  // TODO delete if client agreed
  // const looseOnSaleAssets = getValues(
  //   concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.LOSS_ON_SALE_OF_ASSETS),
  // );
  const investingNetCash = useWatch({ name: totalName }) ?? getValues(totalName);

  const nonOperationIncomeField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_INCOME);

  const tableValues = useWatch({ name: tableName });

  const nonOperationIncomeHandler = useCallback((values: IPeriodsTableRows[]) => {
    const nonOperationIncome = summarizeSubtractPeriodValues([...values.slice(2)], PERIODS_OPERATION_TYPES.ADD);
    setValue(nonOperationIncomeField, [nonOperationIncome]);
  }, []);

  useDebouncedSave({
    data: tableValues,
    saveFunction: nonOperationIncomeHandler,
    debounceTime: DEFAULT_DEBOUNCE_TIME_DIRECT_FORM,
    skipExecutionOnFirstRender: true,
  });

  return (
    <Grid container direction={'column'}>
      <Grid item xs>
        <TableSectionTitle title={t('financialPlan.projectedCashFlow.direct.investingActivities.title')} />
      </Grid>
      <Grid item xs>
        <ReadOnlyTableRow
          name={concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.PURCHASE_PROPERTY_PLANT_EQUIPMENT)}
          columnValues={getEachRowValues([purchasePPE], 0, period, true)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          rowTitle={investingActivitiesLinkedFields[INVESTING_ACTIVITIES_NAMES.PURCHASE_PROPERTY_PLANT_EQUIPMENT].title}
          noNeedName={true}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs key={key}>
        {firstGroupFields.map((rowItem, index) => (
          <FieldTableRow
            key={rowItem.id}
            name={tableName}
            rowIndex={index}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            disabled={disabled}
            rowTitle={investingActivitiesVariants[index]}
            dataAttributeWithIndex
            needToConvertToNegative
          />
        ))}
        {/* 
// TODO delete if client agreed
        <Grid item xs>
          <ReadOnlyTableRow
            name={concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.LOSS_ON_SALE_OF_ASSETS)}
            columnValues={getEachRowValues([looseOnSaleAssets], 0, period)}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            rowTitle={investingActivitiesLinkedFields[INVESTING_ACTIVITIES_NAMES.LOSS_ON_SALE_OF_ASSETS].title}
            noNeedName={true}
            disabled={disabled}
          />
        </Grid> */}

        {secondGroupFields.map((rowItem, index) => {
          const newIndex = index + firstGroupFields.length;
          // newIndex === 2 corresponds to "Proceeds from sale of property, plant, and equipment"
          const highlightedColumns =
            newIndex === 2 && excessiveProceedsFromSalePeriods.size > 0
              ? excessiveProceedsFromSalePeriods
              : undefined;
          const highlightedColumnsTooltip =
            newIndex === 2 && excessiveProceedsFromSalePeriods.size > 0
              ? t('financialPlan.projectedCashFlow.direct.investingActivities.excessiveProceedsFromSaleTooltip')
              : undefined;
          return (
            <FieldTableRow
              key={rowItem.id}
              name={tableName}
              rowIndex={newIndex}
              columnsNames={columnsNames || []}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              disabled={disabled}
              rowTitle={investingActivitiesVariants[newIndex]}
              dataAttributeWithIndex
              highlightedColumns={highlightedColumns}
              highlightedColumnsTooltip={highlightedColumnsTooltip}
            />
          );
        })}

        <TotalRowComponent
          name={totalName}
          columnValues={getEachRowValues([investingNetCash], 0, period, true)}
          firstColumnWidth={FIRST_COLUMN_WIDTH}
          totalRowTitle={totalRowTitle}
          disabled={disabled}
          marginBottomSize="none"
        />
      </Grid>
    </Grid>
  );
};
